import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ShieldCheck, Copy, Check, ChevronRight, PlayCircle, Loader2, FileText,
  CheckCircle2, AlertTriangle, CircleDashed, Sparkles, Download, Terminal,
  Workflow, KeyRound, Clock,
} from "lucide-react";
import { Tile, Badge } from "./Primitives";
import {
  downloadAttestationPdf, kickAutoDiscover, listBronze,
} from "../lib/s3Api";
import { useRole } from "../lib/role.jsx";
import { cn } from "../lib/utils";

// -----------------------------------------------------------------------------
// 1Rotary Handshake · Pre-flight Checklist
// Three live-tracked steps the IT Head follows to go from "connection
// seeded" → "Bronze catalog populated". Each step has:
//   • A status chip that reflects the real connection / bronze state.
//   • A right-side action that advances it (Copy, Test now, Discover now).
// Also exposes an "Attestation PDF" button for the CFO handoff.
// -----------------------------------------------------------------------------

const ROLE_ARN = "arn:aws:iam::691208917903:role/OneRotaryEctOdtReadRole";
const EXTERNAL_ID = "1rotary-pilot-ect-odt-2026";
const BUCKET = "1rotary-ect-odt-pilot";
const PREFIX = "1rotary/pilot/";
const REGION = "ap-southeast-1";

function trustPolicy(backendRoleArn) {
  return JSON.stringify({
    Version: "2012-10-17",
    Statement: [
      {
        Sid: "AsteriskControlTowerAssumeRole",
        Effect: "Allow",
        Principal: {
          AWS: backendRoleArn || "<Asterisk-published-role-ARN>",
        },
        Action: "sts:AssumeRole",
        Condition: {
          StringEquals: { "sts:ExternalId": EXTERNAL_ID },
        },
      },
    ],
  }, null, 2);
}

// Compact relative time ("3m ago", "2h ago"). Used by the expired-token
// recovery banner so operators see how stale the failure is at a glance.
function formatRelativeTime(iso) {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const diffSec = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

export default function PilotPreflightChecklist({
  connection, backendHealth, onTest, onReload, onPop, onRotate,
}) {
  const { user } = useRole();
  const connected = connection?.status === "connected";
  const everTested = !!connection?.last_test_ts;

  const [bronzeCount, setBronzeCount] = useState(null);
  const [bronzeLoading, setBronzeLoading] = useState(false);
  const [discoverBusy, setDiscoverBusy] = useState(false);
  const [attestBusy, setAttestBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [policyCopied, setPolicyCopied] = useState(false);
  const [extCopied, setExtCopied] = useState(false);
  const [arnCopied, setArnCopied] = useState(false);

  // ---- Live countdown driven by connection.aws_session_expiry_utc ---------
  // Repaints every 30s so the "token expires in Xm" badge stays accurate
  // without hammering the backend.
  const [nowTs, setNowTs] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowTs(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  const expiryIso = connection?.aws_session_expiry_utc || null;
  const expiryInfo = useMemo(() => {
    if (!expiryIso) return null;
    const exp = Date.parse(expiryIso);
    if (Number.isNaN(exp)) return null;
    const diffMin = Math.round((exp - nowTs) / 60_000);
    let state = "ok";
    if (diffMin <= 0) state = "expired";
    else if (diffMin <= 15) state = "critical";
    else if (diffMin <= 45) state = "warning";
    let label;
    if (diffMin > 0) label = diffMin < 60 ? `expires in ${diffMin}m` : `expires in ${Math.floor(diffMin / 60)}h ${diffMin % 60}m`;
    else label = diffMin < 0 ? `expired ${Math.abs(diffMin)}m ago` : "expired";
    return { diffMin, state, label, expIso: expiryIso };
  }, [expiryIso, nowTs]);

  const backendRoleArn = backendHealth?.published_role_arn || "";
  const policyJson = useMemo(() => trustPolicy(backendRoleArn), [backendRoleArn]);

  const refreshBronze = useCallback(async () => {
    if (!connection) return;
    setBronzeLoading(true);
    try {
      const r = await listBronze(connection.id);
      setBronzeCount((r.assets || []).length);
    } catch {
      setBronzeCount(0);
    } finally {
      setBronzeLoading(false);
    }
  }, [connection]);

  useEffect(() => { refreshBronze(); }, [refreshBronze]);
  // Poll Bronze count after an auto-discovery kick so the step 3 chip
  // moves to green without a page refresh.
  useEffect(() => {
    if (!connection?.auto_discovery_in_progress) return;
    const t = setInterval(() => { refreshBronze(); onReload?.(); }, 3000);
    return () => clearInterval(t);
  }, [connection?.auto_discovery_in_progress, refreshBronze, onReload]);

  async function copy(value, setter) {
    try {
      await navigator.clipboard.writeText(value);
      setter(true);
      setTimeout(() => setter(false), 1500);
    } catch { /* ignore clipboard permission */ }
  }
  async function onTestClick() {
    if (!connection || testBusy) return;
    setTestBusy(true);
    try { await onTest?.(connection.id); } finally {
      setTestBusy(false);
      refreshBronze();
    }
  }
  async function onDiscover() {
    if (!connection || discoverBusy) return;
    setDiscoverBusy(true);
    try {
      await kickAutoDiscover(connection.id);
      onPop?.("Auto-discovery started · schema inspection running in the background");
      setTimeout(() => { refreshBronze(); onReload?.(); }, 2000);
    } catch (e) {
      onPop?.(`Could not kick discovery: ${e.message}`);
    } finally {
      setDiscoverBusy(false);
    }
  }
  async function onAttestation() {
    if (!connection) return;
    setAttestBusy(true);
    try {
      await downloadAttestationPdf(connection.id, {
        days: 30, actor: user?.employeeId,
      });
    } catch (e) {
      onPop?.(`Attestation failed: ${e.message}`);
    } finally {
      setAttestBusy(false);
    }
  }

  const step1Status = (everTested || connected || policyCopied) ? "done" : "todo";
  const step2Status = connected ? "done"
                   : connection?.last_test_ok === false ? "failing"
                   : testBusy ? "in-progress"
                   : "todo";
  const step3Status = (bronzeCount || 0) > 0 ? "done"
                   : (connection?.auto_discovery_in_progress || discoverBusy) ? "in-progress"
                   : connected ? "ready"
                   : "blocked";
  const allDone = step1Status === "done" && step2Status === "done" && step3Status === "done";

  return (
    <Tile className="col-span-12" testid="pilot-preflight"
          title="1Rotary Handshake · Pre-flight Checklist"
          action={
            <div className="flex items-center gap-2 flex-wrap">
              <Badge status={allDone ? "success" : "warning"}>
                <ShieldCheck size={10} className="inline mr-1" />
                {allDone ? "Handshake complete" : "Awaiting handshake"}
              </Badge>
              {expiryInfo && (
                <Badge
                  testid="pilot-expiry-badge"
                  status={connection?.credentials_expired || expiryInfo.state === "expired" || expiryInfo.state === "critical"
                        ? "critical"
                        : expiryInfo.state === "warning" ? "warning"
                        : "info"}>
                  <Clock size={10} className="inline mr-1" />
                  token {connection?.credentials_expired ? "expired" : expiryInfo.label}
                </Badge>
              )}
              {onRotate && (
                <button data-testid="pilot-rotate-btn" onClick={onRotate}
                        className="px-3 py-1.5 text-[11px] border border-warning/60 text-warning hover:bg-warning hover:text-primary-fg rounded-sm inline-flex items-center gap-1">
                  <KeyRound size={11} />
                  Rotate credentials
                </button>
              )}
              <button data-testid="pilot-attestation-pdf" onClick={onAttestation} disabled={attestBusy}
                      className="px-3 py-1.5 text-[11px] border border-success/60 text-success hover:bg-success hover:text-primary-fg rounded-sm inline-flex items-center gap-1 disabled:opacity-40">
                {attestBusy ? <Loader2 size={11} className="animate-spin" /> : <FileText size={11} />}
                Attestation PDF
              </button>
            </div>
          }>
      {!connection ? (
        <div className="py-6 text-center text-muted text-[12px]">
          The 1Rotary pilot connection has not been seeded yet. Check backend logs for the seed line.
        </div>
      ) : (
        <>
          {/* Facts row — always visible so the IT Head sees exactly what we have provisioned */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4 text-[11px]">
            <Fact label="Role ARN"         value={ROLE_ARN} testid="pilot-fact-role" mono />
            <Fact label="Region"           value={REGION}  testid="pilot-fact-region" mono />
            <Fact label="Bucket · prefix"  value={`s3://${BUCKET}/${PREFIX}`} testid="pilot-fact-bucket" mono />
            <Fact label="Encryption"       value="SSE-S3 (AES256)" testid="pilot-fact-enc" />
          </div>

          {/* ---------- Expired-token recovery banner (Iter 30.1) ---------- */}
          {connection?.credentials_expired && (
            <div data-testid="pilot-expired-banner"
                 className="mb-4 border border-critical/60 bg-critical/5 rounded-sm p-3.5">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-sm bg-critical/15 text-critical flex items-center justify-center shrink-0">
                  <AlertTriangle size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-critical">
                    AWS refused the connection — temporary STS token has expired
                  </div>
                  <div className="text-[11.5px] text-fg/90 mt-1 leading-relaxed">
                    <span className="text-muted">Error:&nbsp;</span>
                    <code className="font-mono-tabular text-critical/90" data-testid="pilot-expired-error">
                      {connection?.credentials_expired_detail || connection?.last_error}
                    </code>
                    {connection?.credentials_expired_at && (
                      <span className="text-muted ml-2">
                        · first seen {formatRelativeTime(connection.credentials_expired_at)}
                      </span>
                    )}
                  </div>
                  <ul className="text-[11px] text-fg/85 mt-2 space-y-1 list-disc list-inside leading-snug">
                    <li>
                      <strong className="text-fg">Recommended fix:</strong> rotate the pilot's three STS values
                      (<code className="font-mono-tabular">AWS_ACCESS_KEY_ID</code>,
                      <code className="font-mono-tabular">AWS_SECRET_ACCESS_KEY</code>,
                      <code className="font-mono-tabular">AWS_SESSION_TOKEN</code>) via the button on the right.
                    </li>
                    <li>
                      Ask 1Rotary's IAM owner for a fresh <code className="font-mono-tabular">sts:AssumeRole</code> response (typically valid 1 h / 3 h / 12 h).
                    </li>
                    <li>
                      After rotation the next AWS probe runs automatically and reconnects the pilot. Pending tables resume Bronze → Silver on schedule.
                    </li>
                  </ul>
                  <div className="flex items-center gap-2 mt-3">
                    {onRotate ? (
                      <button data-testid="pilot-expired-rotate"
                              onClick={onRotate}
                              className="px-3 py-1.5 text-[11.5px] font-semibold bg-critical text-primary-fg hover:opacity-90 rounded-sm inline-flex items-center gap-1.5">
                        <KeyRound size={11} />
                        Rotate credentials now
                      </button>
                    ) : (
                      <span className="text-[11px] text-muted italic">
                        Ask the IT Head to rotate — this control is role-gated.
                      </span>
                    )}
                    <button data-testid="pilot-expired-retest"
                            onClick={() => onTest?.(connection?.id)}
                            disabled={testBusy}
                            className="px-3 py-1.5 text-[11.5px] border border-border rounded-sm hover:bg-surface-2 inline-flex items-center gap-1.5 disabled:opacity-40">
                      {testBusy ? <Loader2 size={11} className="animate-spin" /> : <PlayCircle size={11} />}
                      Re-test connection
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {/* ---------- STEP 1 ---------- */}
            <Step n={1} status={step1Status} testid="pilot-step-1"
                  title="Copy External ID & paste the trust policy into 1Rotary's role"
                  description="This is the only step that happens outside the Control Tower. 1Rotary places the policy below on OneRotaryEctOdtReadRole so our backend's identity can call sts:AssumeRole.">
              <div className="space-y-2 text-[11.5px]">
                <CopyLine label="External ID"
                          value={EXTERNAL_ID} testid="pilot-ext-id"
                          copied={extCopied} onCopy={() => copy(EXTERNAL_ID, setExtCopied)} />
                <CopyLine label="Asterisk backend role ARN (principal)"
                          value={backendRoleArn || "— backend workload identity not configured yet —"}
                          testid="pilot-backend-role" disabled={!backendRoleArn}
                          copied={arnCopied} onCopy={() => copy(backendRoleArn, setArnCopied)} />
                <button data-testid="pilot-toggle-policy"
                        onClick={() => setShowPolicy(v => !v)}
                        className="inline-flex items-center gap-1.5 text-[11px] text-primary hover:opacity-80">
                  <ChevronRight size={11}
                                className={cn("transition-transform", showPolicy && "rotate-90")} />
                  {showPolicy ? "Hide trust policy JSON" : "Show full trust policy JSON"}
                </button>
                {showPolicy && (
                  <div data-testid="pilot-policy-block"
                       className="border border-border rounded-sm bg-bg p-3 relative">
                    <button data-testid="pilot-policy-copy"
                            onClick={() => copy(policyJson, setPolicyCopied)}
                            className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] border border-border bg-surface hover:border-primary hover:text-primary rounded-sm">
                      {policyCopied ? <Check size={10} /> : <Copy size={10} />}
                      {policyCopied ? "Copied!" : "Copy JSON"}
                    </button>
                    <pre data-testid="pilot-policy-json"
                         className="font-mono-tabular text-[10.5px] leading-snug whitespace-pre-wrap break-all pr-20">
{policyJson}
                    </pre>
                    <div className="text-[10.5px] text-muted mt-2 leading-relaxed">
                      <Terminal size={10} className="inline mr-1 text-primary" />
                      1Rotary action: open AWS Console → IAM → Roles → <code className="font-mono-tabular">OneRotaryEctOdtReadRole</code> → Trust relationships → replace with the JSON above.
                    </div>
                  </div>
                )}
              </div>
            </Step>

            {/* ---------- STEP 2 ---------- */}
            <Step n={2} status={step2Status} testid="pilot-step-2"
                  title="Test the connection"
                  description={everTested
                    ? "Last live sts:AssumeRole probe below. The health scheduler re-runs this every 15 minutes."
                    : "Verify the trust policy linkage. Runs sts:AssumeRole + a scoped ListObjectsV2 call against the approved prefix."}>
              <div className="flex flex-wrap items-center gap-3 text-[11.5px]">
                <StatusLine ok={connection.status === "connected"}
                            label={`Current status: ${connection.status}`}
                            testid="pilot-conn-status" />
                {connection.last_test_ts && (
                  <span className="text-muted">
                    Last probe · <span className="font-mono-tabular text-fg">{(connection.last_test_ts || "").slice(0, 19).replace("T", " ")} UTC</span>
                  </span>
                )}
                {connection.consecutive_failures > 0 && (
                  <Badge status={connection.consecutive_failures >= 2 ? "critical" : "warning"}
                         testid="pilot-conn-failures">
                    {connection.consecutive_failures} consecutive failure{connection.consecutive_failures !== 1 ? "s" : ""}
                  </Badge>
                )}
                <button data-testid="pilot-test-now" onClick={onTestClick} disabled={testBusy}
                        className="ml-auto px-3 py-1.5 text-[11px] border border-primary bg-primary/10 text-primary hover:bg-primary hover:text-primary-fg rounded-sm inline-flex items-center gap-1 disabled:opacity-40">
                  {testBusy ? <Loader2 size={11} className="animate-spin" /> : <PlayCircle size={11} />}
                  {testBusy ? "Testing…" : "Test now"}
                </button>
              </div>
              {connection.last_error && (
                <div data-testid="pilot-last-error"
                     className="mt-2 text-[11px] text-critical bg-critical/5 border border-critical/30 p-2 rounded-sm leading-snug">
                  <AlertTriangle size={11} className="inline mr-1" />
                  <span className="font-mono-tabular">{connection.last_error}</span>
                </div>
              )}
            </Step>

            {/* ---------- STEP 3 ---------- */}
            <Step n={3} status={step3Status} testid="pilot-step-3"
                  title="Auto-Discover Bronze catalog"
                  description="On the first-time connected transition the backend lists the approved prefix, inspects each file's schema and stages Bronze assets automatically. You can also force a re-sweep.">
              <div className="flex flex-wrap items-center gap-3 text-[11.5px]">
                <BronzeStatBlock
                  count={bronzeCount}
                  loading={bronzeLoading}
                  autoDiscovery={connection}
                />
                <button data-testid="pilot-discover-now"
                        onClick={onDiscover}
                        disabled={discoverBusy || !connected}
                        title={connected ? "Run auto-discovery now" : "Connection must be connected first"}
                        className="ml-auto px-3 py-1.5 text-[11px] border border-info text-info hover:bg-info hover:text-primary-fg rounded-sm inline-flex items-center gap-1 disabled:opacity-40">
                  {discoverBusy ? <Loader2 size={11} className="animate-spin" />
                                : <Workflow size={11} />}
                  {discoverBusy ? "Discovering…" : "Discover now"}
                </button>
              </div>
              {connection.auto_discovery_detail && (
                <div className="mt-2 text-[11px] text-muted leading-snug">
                  <Sparkles size={10} className="inline mr-1 text-primary" />
                  Last auto-discovery · <span className="font-mono-tabular text-fg">{connection.auto_discovery_detail}</span>
                  {connection.auto_discovery_finished_at && (
                    <>{" · "}
                      <span className="font-mono-tabular">
                        {(connection.auto_discovery_finished_at || "").slice(0, 19).replace("T", " ")} UTC
                      </span>
                    </>
                  )}
                </div>
              )}
            </Step>
          </div>

          <div className="mt-4 text-[10.5px] text-muted leading-snug border-t border-border pt-3">
            <Download size={10} className="inline mr-1 text-success" />
            The Attestation PDF button is a dated, signed record covering the last 30 days of audit entries — forward it to 1Rotary whenever CFO or compliance ask for proof of access.
          </div>
        </>
      )}
    </Tile>
  );
}

function Fact({ label, value, mono, testid }) {
  return (
    <div className="border border-border rounded-sm bg-surface-2/40 p-2.5">
      <div className="text-[9.5px] uppercase tracking-widest text-muted font-semibold">{label}</div>
      <div data-testid={testid}
           className={cn("mt-0.5 text-[11px] leading-snug break-all",
             mono && "font-mono-tabular")}>
        {value}
      </div>
    </div>
  );
}

function Step({ n, status, title, description, testid, children }) {
  const map = {
    done:        { icon: CheckCircle2,  cls: "border-success/40 bg-success/5 text-success",   label: "done" },
    "in-progress": { icon: Loader2,     cls: "border-info/40 bg-info/5 text-info animate-pulse", label: "in progress" },
    ready:       { icon: PlayCircle,    cls: "border-info/40 bg-info/5 text-info",            label: "ready" },
    failing:     { icon: AlertTriangle, cls: "border-critical/40 bg-critical/5 text-critical", label: "failing" },
    blocked:     { icon: CircleDashed,  cls: "border-border bg-surface-2/40 text-muted",      label: "blocked by prior step" },
    todo:        { icon: CircleDashed,  cls: "border-border bg-surface-2/40 text-muted",      label: "to do" },
  };
  const meta = map[status] || map.todo;
  const Icon = meta.icon;
  return (
    <div data-testid={testid}
         className={cn("border rounded-sm p-3", meta.cls)}>
      <div className="flex items-start gap-3">
        <div className="w-7 h-7 shrink-0 rounded-sm bg-surface border border-current inline-flex items-center justify-center font-mono-tabular font-bold text-[13px]">
          {n}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-[12.5px] text-fg">{title}</span>
            <Badge status={status === "done" ? "success"
                        : status === "in-progress" ? "info"
                        : status === "failing" ? "critical"
                        : status === "ready" ? "info"
                        : "neutral"} testid={`${testid}-status`}>
              <Icon size={10} className={cn("mr-1", status === "in-progress" && "animate-spin")} />
              {meta.label}
            </Badge>
          </div>
          <div className="text-[11px] text-muted leading-snug mt-0.5">{description}</div>
          <div className="mt-2.5">{children}</div>
        </div>
      </div>
    </div>
  );
}

function CopyLine({ label, value, testid, copied, onCopy, disabled }) {
  return (
    <div className="flex items-center gap-2 border border-border rounded-sm bg-surface p-2">
      <div className="shrink-0 text-[10px] uppercase tracking-widest text-muted font-semibold w-[180px]">{label}</div>
      <code data-testid={testid}
            className={cn("flex-1 min-w-0 font-mono-tabular text-[11px] break-all",
              disabled && "text-muted italic")}>
        {value}
      </code>
      <button data-testid={`${testid}-copy`} onClick={onCopy} disabled={disabled}
              className="shrink-0 px-2 py-1 text-[10.5px] border border-border hover:border-primary hover:text-primary rounded-sm inline-flex items-center gap-1 disabled:opacity-40">
        {copied ? <Check size={10} className="text-success" /> : <Copy size={10} />}
        {copied ? "Copied!" : "Copy"}
      </button>
    </div>
  );
}

function StatusLine({ ok, label, testid }) {
  return (
    <span data-testid={testid}
          className={cn("inline-flex items-center gap-1 font-mono-tabular", ok ? "text-success" : "text-warning")}>
      {ok ? <CheckCircle2 size={11} /> : <AlertTriangle size={11} />}
      {label}
    </span>
  );
}

function BronzeStatBlock({ count, loading, autoDiscovery }) {
  const status = autoDiscovery?.auto_discovery_status;
  const inProgress = autoDiscovery?.auto_discovery_in_progress;
  return (
    <div className="inline-flex items-center gap-2 text-[11px]">
      <span className="text-muted">Bronze assets:</span>
      <strong className="font-mono-tabular text-fg" data-testid="pilot-bronze-count">
        {loading ? "…" : count ?? "—"}
      </strong>
      {inProgress && <Badge status="info"><Loader2 size={9} className="inline mr-1 animate-spin" />discovering…</Badge>}
      {!inProgress && status === "ok" && (count || 0) > 0 && <Badge status="success">last sweep ok</Badge>}
      {!inProgress && status === "failed" && <Badge status="critical">last sweep failed</Badge>}
    </div>
  );
}
